import React, { useEffect, useState } from 'react';
import { DeleteOutlined, EditOutlined, DownloadOutlined, DislikeOutlined, LikeOutlined } from '@ant-design/icons';
import { Popconfirm, Badge, Button as ButtonAntd, Space } from 'antd';
import { useTranslation } from 'react-i18next';
import Box from '~/components/Box';
import Button from '~/components/Button';
import Pagination from '~/components/Pagination';
import Row from '~/components/Row';
import { Table, TableActions } from '~/components/Table';
import ProposalClientForm from './form';
import Filter from './filter';
import { paginationProposalClients, deleteProposalClient, approveProposal } from '~/services/hooks/proposalClients';
import { updateProposalClient } from '~/services/hooks/proposalClients';
import { downloadFile } from '~/services/hooks/files';
import moment from 'moment';
import errorHandler from '~/Utils/errorHandler';
import ModalSignatureCanvas from '~/components/ModalSignatureCanvas/ModalSignatureCanvas';

export default function ProposalClients({ project, customer }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showSignatureModal, setSignatureModal] = useState(false);
  const [meta, setMeta] = useState({});
  const [tableData, setTableData] = useState([]);
  const [sortBy, setSortBy] = useState('id');
  const [sortDirection, setSortDirection] = useState('DESC');
  const [filters, setFilters] = useState([]);

  const fetchData = async page => {
    setLoading(true);
    const data = await paginationProposalClients({
      page: page - 1,
      sortBy,
      sortDirection,
      filters: [...filters, { columnJoin: 'project', field: 'id', value: project?.id, restriction: 'EQUAL' }],
    });
    setTableData(data.content);
    const metaData = {
      first: data.first,
      last: data.last,
      numberOfElements: data.numberOfElements,
      pageNumber: data.pageable.pageNumber + 1,
      pageSize: data.pageable.pageSize,
      totalElements: data.totalElements,
    };
    setMeta(metaData);
    setLoading(false);
  };

  useEffect(() => {
    if (project.id !== null) {
      fetchData(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, sortBy, project]);

  const handleDelete = async id => {
    await deleteProposalClient(id);
    fetchData(1);
  };

  const handleEdit = record => {
    setSelectedRecord(record);
    setShowForm(true);
  };

  const onUpdateItem = item => {
    const index = tableData.findIndex(i => i.id === item.id);
    if (index > -1) {
      const newData = [...tableData];
      newData[index] = item;
      setTableData(newData);
    }
  };

  // const handleEditItem = async item => {
  //   await updateProposalClient(item);
  //   fetchData(1);
  // };

  const handleDisapproveItem = async item => {
      item.disapproved = true;
      await updateProposalClient(item);
      fetchData(1);
    }
  
    const handleSignatureChange = async ({ name, jobRole, signatureResult, rubricResult, password }) => {
      try {
        const formData = new FormData();
        formData.append('proposalId', selectedRecord.id);
        formData.append('name', name);
        formData.append('jobRole', jobRole);
        formData.append('signature', signatureResult);
        formData.append('rubric', rubricResult);
        formData.append('password', password);
        formData.append('jmcApproveClient', true);
  
        await approveProposal(formData);
  
        fetchData(1);
  
        setSignatureModal(false);
        setFilters([]);
      } catch (error) {
        errorHandler(error);
      }
      setLoading(false);
    }

  const style = {
    breadcrumb: { margin: '16px 0' },
    pageTitle: { marginRight: 'auto', width: '100%' },
    btn: { padding: '0 5px' },
    btnNew: {
      col: { marginLeft: 'auto', paddingBottom: '20px' },
      btn: { marginLeft: 'auto' },
    },
    approved: { backgroundColor: '#52c41a' },
    disapproved: { backgroundColor: '#f5222d' },
    pending: { backgroundColor: '#faad14' }
  };

  const tableColumns = [
    {
      title: t('screens:proposalClient.id'),
      dataIndex: 'id',
      key: 'id',
      align: 'left',
      sorter: true,
      width: 80,
      ellipsis: {
        showTitle: true,
      },
    },
    {
      title: t('screens:proposalClient.description'),
      dataIndex: 'description',
      key: 'description',
      align: 'left',
      sorter: true,
      ellipsis: {
        showTitle: true,
      },
    },
    {
      title: t('screens:proposalClient.projectDescription'),
      dataIndex: 'project',
      key: 'project.description',
      align: 'left',
      sorter: true,
      ellipsis: {
        showTitle: true,
      },
      render: project => {
        return project.description;
      },
    },
    {
      title: t('screens:drawing.createDate'),
      dataIndex: 'audit',
      key: 'audit.createDate',
      align: 'left',
      width: 200,
      sorter: true,
      ellipsis: { showTitle: true },
      render: audit => <>{moment(audit.createdDate).format('YYYY/MM/DD HH:MM:ss')}</>, // render: (audit) => (<>{audit.createDate}</>)
    },
    {
      title: t('screens:contracts.data.status'),
      key: 'disapproved',
      align: 'center',
      sorter: true,
      width: 130,
      ellipsis: {
        showTitle: true,
      },
      render: record => {
        if (record.disapproved) {
          return (<Badge count={t('screens:proposalClient.data.disapproved')} style={style.disapproved} title={'DISAPPROVED'} />);
        } else if (record.proposalSignature !== null) {
          return (<Badge count={t('screens:proposalClient.data.approved')} style={style.approved} title={'APPROVED'} />);
        } else {
          return (<Badge count={t('screens:proposalClient.data.pending')} style={style.pending} title={'PENDING'} />);
        }
      },
    },
    {
      title: `${t('screens:contracts.data.approve')}?`,
      key: 'disapproved',
      align: 'center',
      width: 110,
      ellipsis: {
        showTitle: true,
      },
      render: (record) => (
          <Space>
            <ButtonAntd
              style={style.btn}
              type="primary"
              icon={<LikeOutlined />}
              size='small'
              title={t('Approve the proposal')}
              disabled={record.disapproved === false && record.proposalSignature === null ? false : true}
              onClick={() => {
                setSelectedRecord(record);
                setSignatureModal(true);
              }} />
            <Popconfirm
              key={0}
              icon={false}
              title={t('messages:confirmProposalClientDisapprove')}
              disabled={record.disapproved === false && record.proposalSignature === null ? false : true}
              onConfirm={() => {
                handleDisapproveItem(record);
              }}
              okText={t('messages:yes')}
              cancelText={t('messages:no')}
            >
              <ButtonAntd
                style={style.btn}
                danger
                type="primary"
                icon={<DislikeOutlined />}
                size='small'
                title={t('Disapproved the proposal')}
                disabled={record.disapproved === false && record.proposalSignature === null ? false : true} />
            </Popconfirm>
          </Space>
      ),
    },
    {
      title: t('screens:proposalClient.actions'),
      key: 'actions',
      align: 'center',
      ellipsis: {
        showTitle: true,
      },
      width: 130,
      className: 'table-action',
      render: record => (
        <TableActions>
          <Button style={style.btn} onClick={async () => await downloadFile(record.arquive)} title={t('messages:download')}>
            <DownloadOutlined />
          </Button>
          <Button style={style.btn} onClick={() => handleEdit(record)} title={t('messages:edit')}>
            <EditOutlined />
          </Button>
          <Popconfirm
            icon={false}
            title={t('messages:confirmDelete')}
            onConfirm={() => handleDelete(record.id)}
            okText={t('messages:yes')}
            cancelText={t('messages:no')}
          >
            <Button style={style.btn} title={t('messages:delete')}><DeleteOutlined /></Button>
          </Popconfirm>
        </TableActions>
      ),
    },
  ];

  const handleTableChange = (pagination, filters, sorter) => {
    const sortOrder = sorter.order === 'ascend' ? '' : 'DESC';
    if (sorter.field !== sortBy || sortOrder !== sortDirection) {
      setSortBy(sorter.field);
      setSortDirection(sortOrder);
      fetchData(1);
    }
  };

  return (
    <>
      <Filter isLoading={loading} setFilters={setFilters} />
      <Box>
        <Row>
          {/* <Col span={12} style={style.btnNew.col}>
            <Button
              loading={loading}
              color="primary"
              onClick={() => {
                setSelectedRecord(null);
                setShowForm(true);
              }}
              style={style.btnNew.btn}
            >
              <PlusOutlined />
              {t('screens:proposalClient.btnNew')}
            </Button>
          </Col> */}
        </Row>
        <Table
          bordered
          rowKey="id"
          pagination={false}
          loading={loading}
          columns={tableColumns}
          dataSource={[...tableData]}
          onChange={handleTableChange}
        />
        <Pagination
          onChange={value => {
            fetchData(value);
          }}
          meta={meta}
        />
        <ProposalClientForm
          project={project}
          selectedRecord={selectedRecord}
          visible={showForm}
          onClose={() => setShowForm(false)}
          onInsertItem={item => {
            setTableData([...tableData, item]);
          }}
          onUpdateItem={item => onUpdateItem(item)}
        />
        <ModalSignatureCanvas
          record={selectedRecord}
          visible={showSignatureModal}
          loading={loading}
          canvasProps={{ width: 450, height: 80, className: 'sigCanvas' }}
          onSave={handleSignatureChange}
          onLoad={() => { setLoading(true) }}
          onClose={() => {
            setSignatureModal(false);
            setFilters([]);
          }}
        />
      </Box>
    </>
  );
}
